import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { RedoSupportAuthRpcClientContext } from "@redotech/redo-merchant-app-common/support/redo-support-rpc-client-provider";
import { UserContext } from "@redotech/redo-merchant-app-common/user";
import { isRedoUser } from "@redotech/redo-model/checkouts/trusted-users";
import { MinimalConversation } from "@redotech/redo-model/conversation";
import {
  AllConversationInsights,
  ConversationResolutionSentiment,
} from "@redotech/redo-model/conversation-insights";
import { alertOnFailure, toast } from "@redotech/redo-web/alert";
import {
  RedoBadge,
  RedoBadgeColor,
} from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { Tooltip } from "@redotech/redo-web/tooltip/tooltip";
import { memo, useContext, useState } from "react";
import * as conversationInsightDetailsCss from "./conversation-insight-details.module.css";

export const ConversationInsightDetails = memo(
  function ConversationInsightDetails({
    insights,
    conversationId,
    teamId,
    onInsightsUpdated,
  }: {
    insights: AllConversationInsights;
    conversationId: string;
    teamId: string;
    onInsightsUpdated?: (
      conversationInsights: MinimalConversation["conversationInsights"],
    ) => void;
  }) {
    const user = useContext(UserContext);
    const supportRpcClient = useRequiredContext(
      RedoSupportAuthRpcClientContext,
    );
    const showRegenerateButton =
      user && isRedoUser(user.email) && supportRpcClient;

    const [isRegenerating, setIsRegenerating] = useState(false);

    const handleRegenerateClick = useHandler(async () => {
      if (!supportRpcClient) {
        return;
      }

      setIsRegenerating(true);
      try {
        await alertOnFailure("Failed to regenerate conversation insights")(
          async () => {
            const result =
              await supportRpcClient.regenerateConversationInsights({
                conversationId,
                teamId,
              });

            if (result.conversationInsights) {
              onInsightsUpdated?.(result.conversationInsights as any);
            }

            toast("Conversation insights regenerated successfully", {
              variant: "success",
            });
          },
        );
      } finally {
        setIsRegenerating(false);
      }
    });

    return (
      <Flex dir="column" gap="sm">
        {showRegenerateButton && (
          <Flex justify="flex-end">
            <RedoButton
              hierarchy="secondary"
              onClick={handleRegenerateClick}
              pending={isRegenerating}
              size="xs"
              text="Regenerate insights"
            />
          </Flex>
        )}
        <ConversationSummary summary={insights.summary} />
        <CustomerSentiment
          sentiment={insights.customerSentiment}
          sentimentReasoning={insights.customerSentimentReasoning}
        />
      </Flex>
    );
  },
);

const ConversationSummary = memo(function ConversationSummary({
  summary,
}: {
  summary: string;
}) {
  const [collapsedSummary, setCollapsedSummary] = useState(true);

  return (
    <Flex dir="column" gap="xs">
      <Text fontSize="xs" fontWeight="medium">
        Summary
      </Text>
      <Text
        className={
          collapsedSummary
            ? conversationInsightDetailsCss.collapsedSummary
            : undefined
        }
        fontSize="xs"
        fontWeight="regular"
      >
        {summary}
      </Text>
      <RedoButton
        hierarchy="link_underline"
        onClick={() => setCollapsedSummary(!collapsedSummary)}
        size="sm"
        text={collapsedSummary ? "Show more" : "Show less"}
      />
    </Flex>
  );
});

const CustomerSentiment = memo(function CustomerSentiment({
  sentiment,
  sentimentReasoning,
}: {
  sentiment: ConversationResolutionSentiment;
  sentimentReasoning: string;
}) {
  const { color, text } = getCustomerSentimentBadgeProperties(sentiment);
  return (
    <Flex dir="column" gap="xs">
      <Text fontSize="xs" fontWeight="medium">
        Sentiment
      </Text>
      <Tooltip title={sentimentReasoning}>
        <Flex>
          <RedoBadge color={color} text={text} />
        </Flex>
      </Tooltip>
    </Flex>
  );
});

function getCustomerSentimentBadgeProperties(
  sentiment: ConversationResolutionSentiment,
) {
  let color: RedoBadgeColor;
  switch (sentiment) {
    case ConversationResolutionSentiment.POSITIVE:
      color = "success";
      break;
    case ConversationResolutionSentiment.NEGATIVE:
      color = "error";
      break;
    case ConversationResolutionSentiment.NEUTRAL:
      color = "gray";
      break;
  }

  const sentimentStr = sentiment.toString().toLowerCase();
  const text = sentimentStr.charAt(0).toUpperCase() + sentimentStr.slice(1);

  return { color, text };
}
